import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createNote, fetchNotes } from "../../../api/notes";
import {
  axiosWithToast,
  dateToString,
  mapErrors,
  NewLineFormatter,
} from "../../Utils";
import toast from "react-hot-toast";
import { Form, Formik } from "formik";
import NoteField from "./noteField";
import { ResourceLoading } from "../../utils/ResourceLoading";
import * as Yup from "yup";

export const NotesDeluxeSection = (props) => {
  const { module, module_param } = props;

  const { t } = useTranslation();

  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);

  const postNote = async (values) => {
    return await createNote(values);
  };

  const getNotes = async (page = 1, append = false) => {
    if (page === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    const controller = {
      filters: [
        {
          name: props.module,
          value: props.module_param,
        },
      ],
      page: page,
      per_page: 10,
      query: "",
      sort: "desc",
      sortBy: "updated_at",
      view: "all",
    };

    await fetchNotes(controller)
      .then((res) => {
        var array = [];
        if (res.data && res.data.data && Array.isArray(res.data.data)) {
          res.data.data.map((row, i) => {
            if (row && row.id) {
              const rowParams = {
                ...row,
              };
              array.push(rowParams);
            }
          });
        }

        if (append) {
          setNotes((prevNotes) => [...prevNotes, ...array]);
        } else {
          setNotes(array);
        }

        // Check if there are more pages
        // Look for pagination metadata in the response
        const responseData = res.data;
        let hasMorePages = false;

        // Check if there's pagination metadata
        if (responseData.meta && responseData.meta.last_page) {
          hasMorePages = page < responseData.meta.last_page;
        } else if (responseData.last_page) {
          hasMorePages = page < responseData.last_page;
        } else {
          // Fallback: if we got exactly 10 items, there might be more pages
          hasMorePages = res.data.data && res.data.data.length === 10;
        }

        setHasMore(hasMorePages);
        setIsLoading(false);
        setIsLoadingMore(false);
      })
      .catch((err) => {
        console.error("Error fetching notes:", err);
        setIsLoading(false);
        setIsLoadingMore(false);
      });
  };

  const loadMoreNotes = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    getNotes(nextPage, true);
  };

  useEffect(() => {
    getNotes();
  }, []);

  const initialValues = {
    id: null,
    note: null,
    module: module,
    module_param: module_param,
    submit: null,
  };

  // Show first 2 notes initially, or all notes if showAllNotes is true
  const displayedNotes = showAllNotes ? notes : notes.slice(0, 2);

  // Check if there are more notes to display from the current set OR more pages to load
  const hasMoreToDisplay = (notes.length > 2 && !showAllNotes) || hasMore;

  return (
    <div className="bg-white rounded-md ">
      <div className="px-4 ">
        <div className="flex items-center py-5 w-full">
          <div className="block w-[60%]">
            <h2
              id="applicant-information-title"
              className="text-lg font-medium leading-6 text-gray-900"
            >
              Notater
            </h2>
          </div>
        </div>

        <div className="mt-5">
          <div>
            <ul
              role="list"
              className="divide-y divide-gray-200 sm:max-h-[380px] max-h-[100px] overflow-y-scroll"
            >
              {displayedNotes.filter(note => note && note.id).map((note) => (
                <li key={note.id} className="py-4 ">
                  <div className="space-x-3">
                    <div className="flex-1 space-y-1">
                      <div className="flex flex-col justify-between">
                        <h3 className="text-sm font-medium">
                          {note?.created_by?.displayTitle}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {dateToString(note?.created_at)}
                        </p>
                      </div>
                      <p className="text-sm break-words">
                        <NewLineFormatter value={note?.note} />
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Show "Press more" button if there are more notes to display or more pages to load */}
            {hasMoreToDisplay && (
              <div className="flex justify-center mt-2">
                <button
                  onClick={() => {
                    if (showAllNotes) {
                      // If already showing all loaded notes, load more from database
                      loadMoreNotes();
                    } else {
                      // If showing only 2 notes, show all currently loaded notes
                      setShowAllNotes(true);
                    }
                  }}
                  disabled={isLoadingMore}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-white border-b border-b-blue-300 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingMore ? "Laster..." : "Vis flere"}
                </button>
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <ResourceLoading />
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={Yup.object({
              note: Yup.string().required("Note is required"),
            })}
            onSubmit={(values, helpers) => {
              try {
                axiosWithToast(postNote(values), null, null, (data) => {
                  console.log(data);
                  // Handle different possible response structures
                  const newNote = data.data || data;
                  if (newNote && newNote.id) {
                    setNotes([newNote, ...notes]);
                  } else {
                    // If the response structure is unexpected, refresh the notes
                    getNotes();
                  }
                });
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
                helpers.resetForm(initialValues);
                
                // Reset pagination state when adding a new note
                setCurrentPage(1);
                setShowAllNotes(false);
              } catch (err) {
                toast.error("Something went wrong...");
                console.error(err);
                helpers.setStatus({ success: false });
                helpers.setErrors({ submit: err.message });
                helpers.setSubmitting(false);
              }
            }}
          >
            {(formik) => (
              <Form>
                <div className="grid grid-cols-6 gap-6 mt-5">
                  <div className="col-span-6">
                    <NoteField {...formik} />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};
